// Activity Log tab — normalized event feed (events + admin-events) + active sessions.
// Phase 1: real data from the BFF (GET /api/events, /api/admin-events, /api/sessions).
import { useState, useEffect } from "react";
import { Box, Typography, Button, CircularProgress, useTheme } from "@mui/material";
import { tokens } from "../../../theme";
import PersonAddAlt1OutlinedIcon from "@mui/icons-material/PersonAddAlt1Outlined";
import LoginOutlinedIcon from "@mui/icons-material/LoginOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import { MONO_FONT } from "../data/mockUsersRoles";
import { getActivity, getSessions } from "../api";

const SEVERITY = {
  green: { bg: "rgba(22,163,74,0.1)", fg: "#16a34a" },
  cyan: { bg: "rgba(0,135,180,0.1)", fg: "#0087B4" },
  amber: { bg: "rgba(217,119,6,0.1)", fg: "#d97706" },
  red: { bg: "rgba(220,38,38,0.1)", fg: "#dc2626" },
};

const KIND_ICON = {
  invite: PersonAddAlt1OutlinedIcon,
  login: LoginOutlinedIcon,
  edit: EditOutlinedIcon,
  remove: LogoutOutlinedIcon,
};

const exportCsv = (rows) => {
  const head = ["timestamp", "actor", "kind", "message"];
  const strip = (s) => String(s || "").replace(/<[^>]+>/g, "");
  const csv = [head.join(",")]
    .concat(
      rows.map((a) =>
        [a.ts, a.actor, a.kind, strip(a.message)]
          .map((v) => `"${String(v).replace(/"/g, '""')}"`)
          .join(",")
      )
    )
    .join("\n");
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = `federatex-activity-${new Date().toISOString().slice(0, 10)}.csv`;
  link.click();
  URL.revokeObjectURL(url);
};

const ActivityTab = ({ showToast }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [activity, setActivity] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [acts, sess] = await Promise.all([
          getActivity(),
          getSessions().catch(() => []),
        ]);
        if (!alive) return;
        setActivity(acts || []);
        setSessions(sess || []);
      } catch (err) {
        console.error("[ActivityTab] load failed:", err);
        if (alive) setError("Could not load activity from the server.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const panel = {
    backgroundColor: "var(--fx-paper)",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 2,
    overflow: "hidden",
  };

  return (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1.7fr 1fr" }, gap: 1.75, alignItems: "start" }}>
      {/* Activity feed */}
      <Box sx={panel}>
        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderBottom: `1px solid ${theme.palette.divider}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600, color: "var(--fx-text-primary)" }}>
            User & role activity
          </Typography>
          <Button
            size="small"
            variant="outlined"
            disabled={!activity.length}
            onClick={() => {
              exportCsv(activity);
              showToast("Exported activity to CSV", "success");
            }}
            sx={{ fontSize: "0.6875rem", py: 0.25 }}
          >
            Export CSV
          </Button>
        </Box>

        <Box sx={{ px: 2 }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
              <CircularProgress size={22} />
            </Box>
          ) : error ? (
            <Typography sx={{ py: 4, textAlign: "center", fontSize: "0.75rem", color: "#dc2626" }}>
              {error}
            </Typography>
          ) : !activity.length ? (
            <Box sx={{ py: 5, textAlign: "center" }}>
              <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600, color: "var(--fx-text-primary)" }}>
                No activity yet
              </Typography>
              <Typography sx={{ fontSize: "0.6875rem", color: colors.grey[500], mt: 0.5, maxWidth: 360, mx: "auto" }}>
                Login events appear after users sign in. Admin operations require{" "}
                <strong>Admin Events</strong> to be enabled in the realm (Realm Settings →
                Events → Admin events config).
              </Typography>
            </Box>
          ) : (
            activity.map((a, i) => {
              const sev = SEVERITY[a.severity] || SEVERITY.cyan;
              const Icon = KIND_ICON[a.kind] || LoginOutlinedIcon;
              return (
                <Box
                  key={a.id || i}
                  sx={{
                    display: "flex",
                    gap: 1.25,
                    alignItems: "flex-start",
                    py: 1.25,
                    borderBottom: i === activity.length - 1 ? "none" : `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: 1,
                      backgroundColor: sev.bg,
                      color: sev.fg,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <Icon sx={{ fontSize: 15 }} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{ fontSize: "0.75rem", color: colors.grey[400], "& strong": { color: "var(--fx-text-primary)", fontWeight: 600 } }}
                      dangerouslySetInnerHTML={{ __html: `<strong>${a.actor}</strong> ${a.message}` }}
                    />
                    <Typography sx={{ fontFamily: MONO_FONT, fontSize: "0.625rem", color: colors.grey[500], mt: 0.2 }}>
                      {a.ts}
                    </Typography>
                  </Box>
                </Box>
              );
            })
          )}
        </Box>
      </Box>

      {/* Active sessions */}
      <Box sx={panel}>
        <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${theme.palette.divider}` }}>
          <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600, color: "var(--fx-text-primary)" }}>
            Active sessions
          </Typography>
          <Typography sx={{ fontSize: "0.625rem", color: colors.grey[500], mt: 0.2 }}>
            Live Keycloak sessions across your organization
          </Typography>
        </Box>
        <Box sx={{ px: 2 }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={20} />
            </Box>
          ) : !sessions.length ? (
            <Typography sx={{ py: 3, textAlign: "center", fontSize: "0.6875rem", color: colors.grey[500] }}>
              No active sessions
            </Typography>
          ) : (
            sessions.map((s, i) => (
              <Box
                key={s.id || i}
                sx={{
                  py: 1.1,
                  borderBottom: i === sessions.length - 1 ? "none" : `1px solid ${theme.palette.divider}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "var(--fx-text-primary)" }}>
                    {s.user}
                  </Typography>
                  <Typography sx={{ fontFamily: MONO_FONT, fontSize: "0.625rem", color: colors.grey[500] }}>
                    {s.ip} · {s.client}
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: "0.625rem", color: colors.grey[500], whiteSpace: "nowrap" }}>
                  {s.lastAccess}
                </Typography>
              </Box>
            ))
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ActivityTab;
